import { Navbar } from '@/components/layout/Navbar';
import { Footer } from '@/components/sections/Footer';
import { ScrollReveal } from '@/components/effects/ScrollReveal';
import ServiceCalloutBanner from '@/components/shared/ServiceCalloutBanner';
import { Button } from '@/components/ui/button';
import {
  ArrowRight,
  ArrowDown,
  FileX,
  MessageSquareX,
  Search,
  FileEdit,
  MessagesSquare,
  Handshake,
  Trophy,
  Check,
  Rocket,
  GraduationCap,
  TrendingUp,
  RotateCcw,
} from 'lucide-react';
import { useSEO, SITE_URL } from '@/hooks/useSEO';

const CARD_STYLE = {
  background: 'var(--l1)',
  border: '1px solid var(--b1)',
  boxShadow: '0 1px 0 rgba(255,255,255,0.02) inset',
};

const PROBLEMS = [
  {
    icon: FileX,
    title: 'Sending CVs into the void',
    body: 'A generic CV gets ignored. Hiring managers spend 7 seconds on an application. If your CV does not immediately signal relevance, it is gone.',
  },
  {
    icon: MessageSquareX,
    title: 'Getting interviews but not offers',
    body: 'Interviews are not about your experience. They are about how you communicate it. Most candidates lose offers in the first five minutes.',
  },
  {
    icon: Search,
    title: 'Not knowing where to look',
    body: 'Most roles are never posted publicly. The candidates who get them are the ones who know how to access the hidden job market.',
  },
];

const JOURNEY = [
  { icon: FileEdit,      title: 'Profile Rebuild',           body: 'We rewrite your CV and LinkedIn from scratch — not cleaned up, rebuilt. Positioned for the specific role you want, not a general audience.' },
  { icon: MessagesSquare, title: 'Interview Preparation',    body: 'Mock interviews. Detailed feedback. Targeted preparation for the exact roles you are applying for. No generic advice.' },
  { icon: Handshake,     title: 'Active Placement Support',  body: 'We do not just coach you and wish you luck. We actively help you identify opportunities, approach companies, and get in front of decision makers.' },
  { icon: Trophy,        title: 'Offer and Beyond',          body: 'Salary negotiation, offer evaluation, and onboarding preparation. We stay until you are settled in the role — not just until you sign.' },
];

const FEATURES = [
  'Professional CV rebuild — optimised for ATS and human readers',
  'LinkedIn profile overhaul — positioned to attract inbound interest',
  'Interview coaching — mock sessions with real feedback',
  'Portfolio development and mentoring',
  'Active placement support — not just advice',
  'Direct access to your coach throughout',
  '90 day target timeline to offer',
  'Salary negotiation support',
  'Post-offer onboarding preparation',
];

const AUDIENCE = [
  { icon: Rocket,        title: 'Career Changers',         body: 'You have spent years building skills in one field. Now you want to apply them somewhere better. We will help you make the transition without starting from zero.' },
  { icon: GraduationCap, title: 'Recent Graduates',        body: 'You have the qualification. Now you need the offer. We will build your profile, develop your portfolio, and get you in front of the right employers.' },
  { icon: TrendingUp,    title: 'Stuck in the Same Role',  body: 'You have been applying for promotions or new roles for months. Something is not working. We will find it and fix it.' },
  { icon: RotateCcw,     title: 'Returning to Work',       body: 'Time out of the workforce does not have to be a disadvantage. We will position your experience and your gap as strengths, not liabilities.' },
];

function scrollTo(id) {
  return (e) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
}

export default function LaunchPage() {
  useSEO({
    title: 'Launch | Acodia — Grow. Defend. Be Found.',
    description: 'Career placement service for tech professionals. Profile rebuild, interview preparation, and active placement support — with a 90 day target to offer.',
    path: '/launch',
    ogImage: `${SITE_URL}/images/og/og-launch.png`,
  });

  return (
    <div style={{ background: 'var(--bg)', minHeight: '100vh', color: 'var(--white)' }} data-testid="launch-page">
      <Navbar />

      {/* ── HERO ── */}
      <section
        data-testid="launch-hero"
        className="relative pt-32 pb-20 sm:pt-40 sm:pb-28"
        style={{ background: 'var(--bg)' }}
      >
        <div className="absolute inset-0 pointer-events-none" style={{ background: 'var(--hero-mesh)' }} />
        <div className="relative max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <p className="font-body text-xs uppercase tracking-[0.22em] mb-3" style={{ color: 'var(--a)' }}>
              Acodia Launch
            </p>
            <h1
              className="font-display font-extrabold tracking-[-0.03em] mb-5 max-w-[20ch]"
              style={{ fontSize: 'clamp(2.25rem, 5vw, 4rem)', color: 'var(--white)' }}
            >
              Stop Applying. Start Getting Offers.
            </h1>
            <p className="font-body font-light text-base sm:text-lg max-w-3xl mb-8" style={{ color: 'var(--tm)', lineHeight: '1.72' }}>
              Most job seekers apply for hundreds of roles and hear nothing back. Acodia Launch fixes the real problem —
              your positioning, your profile, and your interview performance — so the right employers find you and
              choose you.
            </p>
            <div className="flex flex-wrap gap-3">
              <a
                href="/pricing?service=launch"
                data-testid="launch-hero-primary"
                className="btn-shimmer inline-flex items-center font-body text-sm h-11 px-6 rounded-[3px]"
                style={{ background: 'var(--a)', color: 'var(--white)' }}
              >
                Start Your Launch <ArrowRight size={14} className="ml-2" />
              </a>
              <Button
                data-testid="launch-hero-secondary"
                onClick={scrollTo('launch-journey')}
                className="font-body text-sm h-11 px-6 rounded-[3px]"
                style={{ background: 'transparent', color: 'var(--tm)', border: '1px solid var(--b1)' }}
              >
                See How It Works <ArrowDown size={14} className="ml-2" />
              </Button>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* ── THE PROBLEM ── */}
      <section data-testid="launch-problem" className="py-14 sm:py-20" style={{ background: 'var(--l2)' }}>
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <h2
              className="font-display font-extrabold tracking-[-0.03em] mb-10 max-w-[24ch]"
              style={{ fontSize: 'clamp(2rem, 4.5vw, 3.2rem)', color: 'var(--white)' }}
            >
              The Job Market Is Not Broken. Your Approach Might Be.
            </h2>
          </ScrollReveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {PROBLEMS.map(({ icon: Icon, title, body }, i) => (
              <ScrollReveal key={title} delay={i * 0.08}>
                <div
                  className="p-6 rounded-lg h-full"
                  style={{
                    ...CARD_STYLE,
                    background: 'var(--danger-10)',
                    borderColor: 'var(--danger)',
                  }}
                >
                  <div
                    className="flex items-center justify-center rounded mb-4"
                    style={{
                      width: 40,
                      height: 40,
                      background: 'rgba(248,113,113,0.15)',
                      border: '1px solid var(--danger)',
                    }}
                  >
                    <Icon size={18} style={{ color: 'var(--danger)' }} />
                  </div>
                  <h3 className="font-display text-lg font-semibold mb-2" style={{ color: 'var(--white)' }}>
                    {title}
                  </h3>
                  <p className="font-body text-sm" style={{ color: 'var(--tm)', lineHeight: '1.65' }}>
                    {body}
                  </p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── THE JOURNEY ── */}
      <section data-testid="launch-journey" id="launch-journey" className="py-14 sm:py-20" style={{ background: 'var(--bg)' }}>
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="text-center mb-12 max-w-3xl mx-auto">
              <h2
                className="font-display font-extrabold tracking-[-0.03em] mb-4"
                style={{ fontSize: 'clamp(2rem, 4.5vw, 3.2rem)', color: 'var(--white)' }}
              >
                From Where You Are to the Offer You Want.
              </h2>
            </div>
          </ScrollReveal>

          <div className="relative">
            {/* Connecting line (desktop only) */}
            <div
              aria-hidden
              className="hidden lg:block absolute left-0 right-0 top-[28px] mx-[10%]"
              style={{ height: 2, background: 'var(--b2)' }}
            />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 relative">
              {JOURNEY.map(({ icon: Icon, title, body }, i) => (
                <ScrollReveal key={title} delay={i * 0.08}>
                  <div className="text-center">
                    <div
                      className="mx-auto flex items-center justify-center rounded-full mb-4 relative"
                      style={{
                        width: 56,
                        height: 56,
                        background: 'var(--a)',
                        border: '4px solid var(--bg)',
                        color: 'var(--white)',
                      }}
                    >
                      <Icon size={22} />
                      <span
                        className="absolute -top-2 -right-2 font-mono text-xs font-bold rounded-full flex items-center justify-center"
                        style={{
                          width: 22,
                          height: 22,
                          background: 'var(--l1)',
                          border: '1px solid var(--a)',
                          color: 'var(--a)',
                        }}
                      >
                        {i + 1}
                      </span>
                    </div>
                    <h3 className="font-display text-lg font-semibold mb-2" style={{ color: 'var(--white)' }}>
                      {title}
                    </h3>
                    <p className="font-body text-sm" style={{ color: 'var(--tm)', lineHeight: '1.65' }}>
                      {body}
                    </p>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-8 sm:py-10" style={{ background: 'var(--bg)' }}>
        <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-8">
          <ServiceCalloutBanner
            testId="launch-callout-banner"
            text="Want expert guidance through this process? Our coaches have helped hundreds of people make this move."
            ctaText="Book a free career consultation →"
            ctaUrl="/pricing?service=launch"
          />
        </div>
      </section>

      {/* ── WHAT YOU GET ── */}
      <section data-testid="launch-features" className="py-14 sm:py-20" style={{ background: 'var(--l2)' }}>
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <h2
              className="font-display font-extrabold tracking-[-0.03em] mb-10 max-w-[24ch]"
              style={{ fontSize: 'clamp(2rem, 4.5vw, 3.2rem)', color: 'var(--white)' }}
            >
              Everything You Need to Go From Application to Offer.
            </h2>
          </ScrollReveal>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 max-w-4xl">
            {FEATURES.map((f, i) => (
              <ScrollReveal key={f} delay={i * 0.04}>
                <li className="flex items-start gap-2 font-body text-sm" style={{ color: 'var(--tm)' }}>
                  <Check size={14} style={{ color: 'var(--a)', marginTop: 3, flexShrink: 0 }} />
                  <span>{f}</span>
                </li>
              </ScrollReveal>
            ))}
          </ul>
        </div>
      </section>

      {/* ── AUDIENCE ── */}
      <section data-testid="launch-audience" className="py-14 sm:py-20" style={{ background: 'var(--bg)' }}>
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <h2
              className="font-display font-extrabold tracking-[-0.03em] mb-10 max-w-[30ch]"
              style={{ fontSize: 'clamp(2rem, 4.5vw, 3.2rem)', color: 'var(--white)' }}
            >
              Acodia Launch Is for People Who Are Done Waiting.
            </h2>
          </ScrollReveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {AUDIENCE.map(({ icon: Icon, title, body }, i) => (
              <ScrollReveal key={title} delay={i * 0.06}>
                <div className="p-6 rounded-lg h-full" style={CARD_STYLE}>
                  <div
                    className="flex items-center justify-center rounded mb-4"
                    style={{
                      width: 36,
                      height: 36,
                      background: 'var(--a-glow)',
                      border: '1px solid var(--a-border)',
                    }}
                  >
                    <Icon size={16} style={{ color: 'var(--a)' }} />
                  </div>
                  <h3 className="font-display text-base font-semibold mb-2" style={{ color: 'var(--white)' }}>
                    {title}
                  </h3>
                  <p className="font-body text-sm" style={{ color: 'var(--tm)', lineHeight: '1.65' }}>
                    {body}
                  </p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ── */}
      <section data-testid="launch-cta" className="py-16 sm:py-24" style={{ background: 'var(--l2)' }}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <ScrollReveal>
            <Trophy size={28} style={{ color: 'var(--a)', margin: '0 auto 16px' }} />
            <h2
              className="font-display font-extrabold tracking-[-0.03em] mb-4"
              style={{ fontSize: 'clamp(1.75rem, 4vw, 2.75rem)', color: 'var(--white)' }}
            >
              Most of Our Clients Receive Their First Offer Within 90 Days.
            </h2>
            <p className="font-body font-light text-base mb-8" style={{ color: 'var(--tm)', lineHeight: '1.72' }}>
              Tell us where you are and we will tell you exactly how we can help.
            </p>
            <a
              href="/pricing?service=launch"
              data-testid="launch-cta-button"
              className="btn-shimmer inline-flex items-center font-body text-sm h-12 px-7 rounded-[3px]"
              style={{ background: 'var(--a)', color: 'var(--white)' }}
            >
              Book Your Free Consultation <ArrowRight size={14} className="ml-2" />
            </a>
          </ScrollReveal>
        </div>
      </section>

      <Footer />
    </div>
  );
}
